/* ============================================================
   ZNAKI · utils.js
   Matematyka, deterministyczne RNG, easing, szum wartości.
   Wszystko deterministyczne — ta sama kompozycja przy tym
   samym ziarnie (seed), niezależnie od klatek i scrolla.
   ============================================================ */

export const TAU = Math.PI * 2;

export const clamp   = (v, a, b) => v < a ? a : (v > b ? b : v);
export const clamp01 = (v) => v < 0 ? 0 : (v > 1 ? 1 : v);
export const lerp    = (a, b, t) => a + (b - a) * t;
export const remap   = (v, a, b, c, d) => c + (d - c) * clamp01((v - a) / (b - a));
export const smoothstep = (a, b, v) => { const t = clamp01((v - a) / (b - a)); return t * t * (3 - 2 * t); };

/* --- easing ------------------------------------------------ */
export const easeOutCubic   = (t) => 1 - Math.pow(1 - t, 3);
export const easeInCubic    = (t) => t * t * t;
export const easeInOutCubic = (t) => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
export const easeOutQuint   = (t) => 1 - Math.pow(1 - t, 5);
/* silnie zwalniający ease-out o regulowanej potędze:
   p=3 ≈ power2.out, p=4 ≈ power3.out, p=5 ≈ power4.out (nomenklatura GSAP) */
export const easeOutPow = (t, p) => 1 - Math.pow(1 - t, p);
/* przestrzelenie — im większe s, tym mocniejszy "odbój" */
export const easeOutBack = (t, s) => {
  const c = (s === undefined ? 1.7 : s);
  const u = t - 1;
  return 1 + (c + 1) * u * u * u + c * u * u;
};

/* --- deterministyczne RNG ---------------------------------- */

/* xmur3: string -> funkcja zwracająca kolejne 32-bitowe ziarna */
export function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

/* mulberry32: szybki PRNG [0,1) z 32-bitowego ziarna */
export function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* strumień RNG dla pod-klucza kompozycji, np. rngFor("A1B2C","heavy",4) */
export function rngFor(seed, tag, index) {
  const s = xmur3(seed + '·' + tag + '·' + index);
  return mulberry32(s());
}

/* pojedyncza deterministyczna wartość [0,1) z liczby całkowitej */
export function hash01(i) {
  let x = i | 0;
  x = Math.imul(x ^ (x >>> 16), 2246822507);
  x = Math.imul(x ^ (x >>> 13), 3266489909);
  x = x ^ (x >>> 16);
  return (x >>> 0) / 4294967296;
}

/* --- szum wartości 1D (płynny, deterministyczny) ------------ */
export function noise1(x, seed) {
  const s = (seed | 0) * 7919;
  const i = Math.floor(x);
  const f = x - i;
  const a = hash01(i + s);
  const b = hash01(i + 1 + s);
  const t = f * f * (3 - 2 * f);
  return a + (b - a) * t; /* [0,1] */
}

/* szum dwuoktawowy, wyśrodkowany na 0, zakres ~[-1,1] */
export function wobble(x, seed) {
  return (noise1(x, seed) - .5) * 1.6 + (noise1(x * 2.7, seed + 13) - .5) * 0.6;
}

/* --- pomocnicze wybory ------------------------------------- */
export const rr = (rng, a, b) => a + (b - a) * rng();                 /* zakres */
export const pick = (rng, arr) => arr[Math.floor(rng() * arr.length) % arr.length];
export const sign = (rng) => rng() < .5 ? -1 : 1;

/* wybór ważony: rw(rng, [['a',3],['b',1]]) */
export function rw(rng, pairs) {
  let total = 0;
  for (const p of pairs) total += p[1];
  let r = rng() * total;
  for (const p of pairs) { r -= p[1]; if (r <= 0) return p[0]; }
  return pairs[pairs.length - 1][0];
}

/* --- formatowanie ------------------------------------------ */
export const pad = (n, w) => String(Math.max(0, Math.floor(n))).padStart(w, '0');

export function randomSeed() {
  const abc = 'ABCDEFGHJKMNPQRSTUWXYZ23456789';
  let s = '';
  for (let i = 0; i < 5; i++) s += abc[Math.floor(Math.random() * abc.length)];
  return s;
}
